import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import useInLightMode from '../../hooks/useInLightMode';
import { Weather } from '../../types/models/Weather';
import {
  darkModeCrossPolColor,
  lightModeCrossPolColor,
  lightModeTraceColor,
} from '../spectrumChart/utils/colors';
import { MeasurementData } from '../weather/types';
import {
  getChartTitleForMeasurementType,
  getLabelForMeasurementType,
  getUnitForMeasurementType,
} from '../weather/utils/dataConverters';
import { useGetChartOptions } from './hooks/useGetWeatherChartOptions';
import 'chartjs-adapter-date-fns';
import {
  convertSecondaryWeatherData,
  convertWeatherData,
  formatSingularPluralRangeLabels,
  getSecondaryDataType,
  getTimeUnitForYAxis,
} from './utils/dataConverters';
import { memo } from 'react';
import { Grid } from '@mui/material';
import { useImperialMeasurements } from './hooks/useImperialMeasurements';
import { useAppSelector } from 'app/hooks';
import WeatherChartAnnotations from './WeatherChartAnnotations';
import { HourlyData } from 'types/models/ForecastDto';

interface Props {
  weatherData: (Weather | HourlyData)[];
  measurement: MeasurementData;
  locationName: string | undefined;
  durationLabel: string;
  spanGaps?: boolean;
  wallId?: number;
}

const WeatherChart = memo(
  ({
    weatherData,
    measurement,
    locationName,
    durationLabel,
    spanGaps,
    wallId,
  }: Props) => {
    const inLightMode = useInLightMode();
    const convertedWeatherData = useImperialMeasurements(weatherData);
    const inImperial = useAppSelector(
      (state) => state.auth.user?.us_customary_units
    );
    const xAxisUnit = getTimeUnitForYAxis(convertedWeatherData);
    const yAxisMin =
      measurement === 'wind_kph' ||
      measurement === 'gust_kph' ||
      measurement === 'cloud_percent' ||
      measurement === 'precip_mm' ||
      measurement === 'humidity'
        ? 0
        : undefined;
    const yAxisMax =
      measurement === 'humidity' || measurement === 'cloud_percent'
        ? 100
        : undefined;
    const chartOptions = useGetChartOptions(
      getUnitForMeasurementType(measurement, inImperial),
      locationName +
        ' ' +
        getChartTitleForMeasurementType(measurement) +
        ' ' +
        formatSingularPluralRangeLabels(durationLabel),
      yAxisMin,
      yAxisMax,
      xAxisUnit
    );
    const data = convertWeatherData(convertedWeatherData, measurement);
    const datasets = [
      {
        label: getLabelForMeasurementType(measurement, inImperial),
        data: data,
        borderJoinStyle: 'round' as 'round' | 'miter' | 'bevel',
        spanGaps: spanGaps ? true : false,
        backgroundColor: inLightMode ? lightModeTraceColor : 'yellow',
        borderColor: inLightMode ? lightModeTraceColor : 'yellow',
      },
    ];
    const secondaryData = convertSecondaryWeatherData(
      convertedWeatherData,
      measurement
    );
    if (secondaryData) {
      datasets.push({
        label: getLabelForMeasurementType(
          getSecondaryDataType(measurement),
          inImperial
        ),
        data: secondaryData,
        spanGaps: spanGaps ? true : false,
        borderJoinStyle: 'round' as 'round' | 'miter' | 'bevel',
        backgroundColor: inLightMode
          ? lightModeCrossPolColor
          : darkModeCrossPolColor,
        borderColor: inLightMode
          ? lightModeCrossPolColor
          : darkModeCrossPolColor,
      });
    }
    return (
      <Grid item container justifyContent='center'>
        <Grid item container style={{ width: '90%' }}>
          <Line
            data={{
              datasets: datasets,
            }}
            // @ts-ignore
            options={chartOptions}
          />
        </Grid>
        <WeatherChartAnnotations
          convertedWeatherData={convertedWeatherData}
          isSecondaryData={!!secondaryData}
          measurement={measurement}
          wallId={wallId}
        />
      </Grid>
    );
  }
);

export default WeatherChart;
